import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { Users, TrendingUp, AlertTriangle, Clock } from "lucide-react";
import { startOfWeek, endOfWeek, isWithinInterval } from "date-fns";

const getDisplayName = (user) => {
  if (user.first_name || user.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user.full_name || user.email;
};

export default function CapacityDashboard({ users, timeEntries, tasks }) {
  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });

  // Calculate capacity metrics for each user
  const userCapacityData = users.map(user => {
    const capacity = user.weekly_capacity || 37.5;
    
    // Actual hours logged this week
    const weekTimeEntries = timeEntries.filter(entry => {
      if (entry.user_email !== user.email) return false;
      const entryDate = new Date(entry.start_time);
      return isWithinInterval(entryDate, { start: weekStart, end: weekEnd });
    });
    const actualHours = weekTimeEntries.reduce((sum, entry) => sum + (entry.duration_minutes || 0) / 60, 0);

    // Allocated hours (estimated from tasks)
    const userTasks = tasks.filter(
      t => t.assigned_to === user.email && t.status !== "completed" && t.status !== "cancelled"
    );
    const allocatedHours = userTasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);

    const utilization = (actualHours / capacity) * 100;
    const allocation = (allocatedHours / capacity) * 100;

    return {
      name: getDisplayName(user),
      email: user.email,
      capacity,
      actualHours: parseFloat(actualHours.toFixed(1)),
      allocatedHours: parseFloat(allocatedHours.toFixed(1)),
      utilization: parseFloat(utilization.toFixed(1)),
      allocation: parseFloat(allocation.toFixed(1)),
      availableHours: parseFloat((capacity - allocatedHours).toFixed(1)),
    };
  }).sort((a, b) => b.utilization - a.utilization);

  // Overall metrics
  const totalCapacity = userCapacityData.reduce((sum, u) => sum + u.capacity, 0);
  const totalActual = userCapacityData.reduce((sum, u) => sum + u.actualHours, 0);
  const totalAllocated = userCapacityData.reduce((sum, u) => sum + u.allocatedHours, 0);
  const totalAvailable = userCapacityData.reduce((sum, u) => sum + u.availableHours, 0);
  const overallocated = userCapacityData.filter(u => u.allocation > 100).length;

  // Chart data
  const chartData = userCapacityData.map(u => ({
    name: u.name.split(' ')[0] || u.name,
    Kapasitet: u.capacity,
    Allokert: u.allocatedHours,
    Faktisk: u.actualHours,
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-5 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Kapasitet</p>
              <p className="text-2xl font-bold text-slate-900">{totalCapacity}h</p>
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Faktisk Brukt</p>
              <p className="text-2xl font-bold text-slate-900">{totalActual.toFixed(0)}h</p>
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Tilgjengelig</p>
              <p className="text-2xl font-bold text-slate-900">{totalAvailable.toFixed(0)}h</p>
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-white border-0 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-rose-100 to-rose-200 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Overallokert</p>
              <p className="text-2xl font-bold text-slate-900">{overallocated}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Capacity Chart */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Kapasitetsfordeling</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" stroke="#64748b" />
            <YAxis stroke="#64748b" />
            <Tooltip />
            <Legend />
            <Bar dataKey="Kapasitet" fill="#cbd5e1" />
            <Bar dataKey="Allokert" fill="#f59e0b" />
            <Bar dataKey="Faktisk" fill="#10b981" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Detailed User List */}
      <Card className="p-6 bg-white border-0 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Detaljert Kapasitet</h3>
        <div className="space-y-4">
          {userCapacityData.map(user => (
            <div key={user.email} className="p-4 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700">
                      {user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900">{user.name}</p>
                    <p className="text-sm text-slate-500">{user.email}</p>
                  </div>
                </div>
                <Badge className={user.allocation > 100 ? "bg-rose-100 text-rose-700" : user.allocation > 80 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}>
                  {user.allocation.toFixed(0)}% allokert
                </Badge>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-3 text-sm">
                <div>
                  <p className="text-slate-500">Kapasitet</p>
                  <p className="font-semibold text-slate-900">{user.capacity}h</p>
                </div>
                <div>
                  <p className="text-slate-500">Allokert</p>
                  <p className="font-semibold text-amber-600">{user.allocatedHours}h</p>
                </div>
                <div>
                  <p className="text-slate-500">Faktisk</p>
                  <p className="font-semibold text-emerald-600">{user.actualHours}h</p>
                </div>
              </div>

              <Progress value={Math.min(user.allocation, 100)} className="h-2" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}